import { EventEmitter, OnDestroy } from '@angular/core';
import { Component, Input, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { map } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { CustomViewService } from '../../services/custom-view.service';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import {
  ModalConfirmationComponent,
  ModalConfirmationModel
} from '../../../../shared-app-portal/components/modal-confirmation/modal-confirmation.component';
import { CustomView } from '../odata-filter/models/custom-view';
import { ViewContext } from '../odata-filter/models/view-context';
import { TenantPlanErrorModalComponent } from '../tenant-plan-error-modal/tenant-plan-error-modal.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'lineup-views-menu',
  templateUrl: './custom-view-menu.component.html',
  styleUrls: ['./custom-view-menu.component.scss'],
  standalone: false
})
export class CustomViewMenuComponent implements OnInit, OnDestroy {
  @Input() selectedView: CustomView;
  @Input() title: string;
  @Input() editable: boolean = true;
  @Input() hideSearch: boolean = false;
  @Input() viewContextId?: string;
  @Output() selectChanged = new EventEmitter<CustomView>();
  @Output() create = new EventEmitter();
  @Output() edit = new EventEmitter<CustomView>();
  @Output() saveChange = new EventEmitter();
  @Output() reset = new EventEmitter<CustomView>();
  private _views: CustomView[] = [];
  private _viewContext: ViewContext;
  private _subs: Subscription[] = [];
  protected get filterdViews() {
    if (this.searchText) {
      return this._views.filter((x) =>
        x.name.toLowerCase().includes(this.searchText.toLowerCase())
      );
    }
    return this._views;
  }
  protected confirmationRef: MatDialogRef<ModalConfirmationComponent>;

  searchText: string;
  constructor(
    private customViewService: CustomViewService,
    route: ActivatedRoute,
    private authService: AuthService,
    private appConfigService: AppConfigService,
    private dialog: MatDialog
  ) {
    if (route?.snapshot?.data) {
      this._viewContext = route.snapshot.data['viewContext'];

      if (!this._viewContext) {
        if (route?.firstChild?.snapshot?.data) {
          this._viewContext = route.firstChild.snapshot.data['viewContext'];
        }
      }
    }
  }

  ngOnInit(): void {
    this._subs.push(
      this.customViewService.resetViewRequested.subscribe(() => {
        this._views.forEach((x) => {
          x.isSelected = false;
          x.isModified = false;
          if (x.isSystemView) {
            x.columns = [];
          }
        });
        this.selectedView = this._views.find((x) => x.isDefault);
        if (this.selectedView) {
          this.selectedView.isSelected = true;
          this.selectChanged.emit(this.selectedView);
        }
      }),
      this.customViewService.customViews
        .pipe(
          map((result) => {
            if (!result) return [];

            let views = this.customViewService.getContextViews(
              result,
              this._viewContext,
              this.viewContextId
            );
            if (views.length > 0) {
              const defaultView = this.customViewService.getDefaultView(views);
              const firstSystemView = views.find((x) => x.isSystemView);
              if (firstSystemView && defaultView === firstSystemView) {
                firstSystemView.isDefault = true;
              } else if (firstSystemView && !defaultView?.isSystemView) {
                firstSystemView.isDefault = false;
              }
              return views;
            } else {
              return null;
            }
          })
        )
        .subscribe((views) => {
          if (views) {
            this._views = views;
            if (!this.selectedView) {
              this.selectChanged.emit(this._views.find((x) => x.isDefault));
            } else {
              let index = this._views.findIndex((x) => x.id == this.selectedView.id);
              if (index != -1) {
                this._views[index].isSelected = true;
              }
            }
          }
        }),

      this.customViewService.customViewCreated.subscribe((result) => {
        if (result && result.viewContext == this._viewContext) {
          this._views.push(result);
          this._views.forEach((x) => (x.isSelected = false));
          this.selectChanged.emit(result);
        }
      }),
      this.customViewService.customViewUpdated.subscribe((result) => {
        if (result && result.viewContext == this._viewContext) {
          let index = this._views.findIndex((x) => x.id == result.id);
          if (index != -1) {
            this._views[index] = result;
          }
          this._views.forEach((x) => (x.isSelected = false));
          this.selectChanged.emit(result);
        }
      }),
      this.customViewService.customViewDeleted.subscribe((result) => {
        if (result && result.viewContext == this._viewContext) {
          let index = this._views.findIndex((x) => x.id == result.id);
          this._views.forEach((x) => (x.isSelected = false));
          if (index != -1) {
            this._views.splice(index, 1);
          }
          if (result.isDefault) {
            this._views.filter((x) => x.isSystemView)[0].isDefault = true;
            this.customViewService.clearDefaultView(result.viewContext);
          }
          this.selectedView = this._views.find((x) => x.isDefault);
          this.selectChanged.emit(this.selectedView);
        }
      })
    );
  }
  public get isReady() {
    return this.selectedView && this.authService.userProfile;
  }
  get systemViews() {
    return this.filterdViews.filter((x) => x.isSystemView);
  }

  get myViews() {
    return this.filterdViews.filter((x) => x.auth0UserId == this.authService.userProfile.userId);
  }

  get sharedViews() {
    return this.filterdViews.filter(
      (x) => x.auth0UserId != this.authService.userProfile.userId && !x.isSystemView
    );
  }
  canRemoveOrEdit(view: CustomView): boolean {
    return !view.isSystemView && this.isOwnerOrStaff;
  }

  get canMarkAsDefault(): boolean {
    return (
      this.editable &&
      ![
        ViewContext.AllCredentialingDetailsReport,
        ViewContext.CredentialingDetailsReport,
        ViewContext.PersonCredentialApplicationReport,
        ViewContext.PersonScenarioReport,
        ViewContext.AllPaymentRecordsReport,
        ViewContext.CredentialPaymentRecordsReport,
        ViewContext.AllInvoiceReport,
        ViewContext.TeamSurvey,
        ViewContext.AllTeamCredentialingDetailsReport,
        ViewContext.TeamCredentialingDetailsReport,
        ViewContext.TeamCredentialApplicationReport,
        ViewContext.TeamScenarioReport
      ].includes(this._viewContext)
    );
  }

  get doesPlanAllowSavingViews(): boolean {
    return this.appConfigService.tenant.plan.canSaveViews;
  }

  onClose() {
    this.searchText = '';
  }
  onSelectView(view: CustomView) {
    this._views.forEach((view) => {
      view.isSelected = false;
    });
    this.selectChanged.emit(view);
  }

  onDelete(view: CustomView) {
    if (!this.canRemoveOrEdit) return;
    let data: ModalConfirmationModel = {
      simpleDialog: false,
      title: null,
      action: `delete ${view.name}`,
      delete: true,
      okButtonText: 'Delete',
      okButtonId: 'modal-custom-view',
      description: null,
      textMatcherValue: ''
    };
    this.confirmationRef = this.dialog.open(ModalConfirmationComponent, {
      data: data
    });

    this.confirmationRef.afterClosed().subscribe((result) => {
      if (result) {
        this.customViewService.deleteView(view);
      }
      this.confirmationRef = null;
    });
  }

  setDefault(view: CustomView) {
    if (!this.canMarkAsDefault) return;
    if (!view.isDefault) {
      let oldDefaultViewIndex = this._views.findIndex((x) => x.isDefault);
      this.customViewService.setDefault(view).subscribe((result) => {
        //rollback the change if set Default was not successful
        if (!result) {
          this._views.forEach((x) => (x.isDefault = false));
          this._views[oldDefaultViewIndex].isDefault = true;
        }
      });

      this._views.forEach((x) => (x.isDefault = false));
      view.isDefault = true;
    }
  }
  private get isOwnerOrStaff(): boolean {
    return this.authService.isOwner() || this.authService.isStaff();
  }

  openTenantPlanDialog() {
    TenantPlanErrorModalComponent.openDialog(
      this.dialog,
      'Save your own views',
      `All those filters you keep applying over and over again? Upgrade your plan and you'll be able to save views. Views can be private (for your eyes only), or shared with your colleagues.`,
      true
    );
  }

  ngOnDestroy(): void {
    this._subs.forEach((sub) => sub.unsubscribe());
  }
}
